import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtCurrency } from "@/lib/format";
import { Layout } from "@/components/Layout";
import { LogoDark } from "@/components/Logo";
import { ReorderReport } from "@/components/ReorderReport";
import { KpiDrilldown } from "@/components/KpiDrilldown";
import { RefillsDashboardCard } from "@/components/RefillsDashboardCard";
import { AutoTextsCardForUser } from "@/components/AutoTextsTodayCard";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Users, TrendingUp, CalendarX, Target, RefreshCw, AlertTriangle, Trophy, ArrowUpRight, Syringe,
} from "lucide-react";

const riskColor = {
  high: "bg-red-50 text-red-700 border-red-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  low: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

function KpiCard({ icon: Icon, label, value, sub, accent, delay, onView, viewLabel, testid }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}
      className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-slate-400 font-semibold">{label}</span>
        <span className={`h-9 w-9 rounded-lg flex items-center justify-center ${accent}`}>
          <Icon className="h-[18px] w-[18px]" />
        </span>
      </div>
      <div className="mt-3 font-mono-metric text-3xl font-bold text-slate-900">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
      {onView && (
        <button data-testid={testid} onClick={onView}
          className="mt-3 text-xs font-semibold text-cyan-700 hover:text-cyan-900 inline-flex items-center gap-1 transition-colors">
          {viewLabel} →
        </button>
      )}
    </motion.div>
  );
}

export default function Dashboard() {
  const qc = useQueryClient();
  const [drill, setDrill] = useState(null);
  const { data: stats } = useQuery({ queryKey: ["stats"], queryFn: async () => (await api.get("/dashboard/stats")).data });
  const { data: funnel = [] } = useQuery({ queryKey: ["funnel"], queryFn: async () => (await api.get("/dashboard/funnel")).data });
  const { data: appts = [] } = useQuery({ queryKey: ["appointments"], queryFn: async () => (await api.get("/appointments")).data });
  const { data: referrals = [] } = useQuery({ queryKey: ["referrals"], queryFn: async () => (await api.get("/referrals")).data });
  const { data: dc } = useQuery({ queryKey: ["drchrono-status"], queryFn: async () => (await api.get("/drchrono/status")).data });
  const { data: procRev } = useQuery({ queryKey: ["proc-revenue"], queryFn: async () => (await api.get("/procedures/revenue")).data });

  const sync = useMutation({
    mutationFn: async () => (await api.post("/drchrono/sync")).data,
    onSuccess: (d) => {
      toast.success(`DrChrono synced · ${d.patients_updated} patients, ${d.appointments_updated} appts updated`);
      qc.invalidateQueries({ queryKey: ["drchrono-status"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["drchrono-sync-status"] }),
    onError: (e) => {
      const msg = e.response?.data?.detail || "Sync failed";
      toast.error(typeof msg === "string" ? msg : "Sync failed");
    },
  });

  const highRisk = appts.filter((a) => a.no_show_risk === "high").slice(0, 5);
  const goalPct = stats ? Math.min(100, Math.round((stats.new_patients / stats.new_patients_goal) * 100)) : 0;

  return (
    <Layout
      title={
        <span className="flex items-center gap-2.5" data-testid="dashboard-title">
          <LogoDark height="h-7" />
          <span>Growth Dashboard</span>
        </span>
      }
      subtitle="Real-time acquisition, retention & revenue signals"
      actions={
        <Button data-testid="drchrono-sync-now-button" onClick={() => sync.mutate()} disabled={sync.isPending}
          className="bg-slate-900 hover:bg-slate-800 text-white">
          <RefreshCw className={`h-4 w-4 mr-2 ${sync.isPending ? "animate-spin" : ""}`} />
          Sync DrChrono
        </Button>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        <KpiCard icon={Users} label="New Patients (MTD)" accent="bg-cyan-50 text-cyan-600" delay={0.02}
          value={stats?.new_patients ?? "—"} sub={`Goal: ${stats?.new_patients_goal ?? 0} · ${goalPct}% reached`}
          onView={() => setDrill("new_patients")} viewLabel="View new patients" testid="kpi-view-new-patients" />
        <KpiCard icon={TrendingUp} label="Projected Revenue" accent="bg-emerald-50 text-emerald-600" delay={0.08}
          value={stats ? fmtCurrency(stats.projected_revenue, true) : "—"} sub="This month, pipeline-weighted" />
        <KpiCard icon={CalendarX} label="No-Show Rate" accent="bg-red-50 text-red-600" delay={0.14}
          value={stats ? `${stats.no_show_rate}%` : "—"} sub={`${stats?.no_shows_90d ?? 0} of ${stats?.past_appointments_90d ?? 0} visits · last 90 days`}
          onView={() => setDrill("no_shows")} viewLabel="View no-shows" testid="kpi-view-no-shows" />
        <KpiCard icon={Target} label="Lead Conversion" accent="bg-blue-50 text-blue-600" delay={0.2}
          value={stats ? `${stats.conversion_rate}%` : "—"} sub={`${fmtCurrency(stats?.pipeline_value || 0, true)} open pipeline`}
          onView={() => setDrill("lead_conversion")} viewLabel="View supporting data" testid="kpi-view-lead-conversion" />
      </div>
      <KpiDrilldown kind={drill} onClose={() => setDrill(null)} />
      <RefillsDashboardCard />
      <AutoTextsCardForUser />

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.24 }}
        data-testid="procedure-revenue-widget"
        className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm mt-6">
        <div className="flex items-center gap-2 mb-4">
          <Syringe className="h-4 w-4 text-cyan-600" />
          <h3 className="font-heading font-bold text-slate-900">Procedure Revenue</h3>
          <span className="text-xs text-slate-400 ml-auto">{fmtCurrency(procRev?.total_revenue || 0, true)} all-time · {(procRev?.log_count ?? 0).toLocaleString()} procedures</span>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-4">
            <div className="text-xs uppercase tracking-wider text-emerald-700/70 font-semibold">This Month</div>
            <div className="mt-1 font-mono-metric text-2xl font-bold text-emerald-700">{fmtCurrency(procRev?.month_revenue || 0)}</div>
          </div>
          <div className="lg:col-span-2">
            <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">Top Procedures</div>
            <div className="space-y-2">
              {(procRev?.by_procedure || []).slice(0, 4).map((t) => (
                <div key={t.name} className="flex items-center gap-3">
                  <div className="min-w-0 flex-1 text-sm font-medium text-slate-800 truncate">{t.name}</div>
                  <div className="text-xs text-slate-400">{t.count}×</div>
                  <div className="font-mono-metric font-semibold text-slate-900 w-24 text-right">{fmtCurrency(t.revenue, true)}</div>
                </div>
              ))}
              {(!procRev || procRev.by_procedure.length === 0) && <p className="text-sm text-slate-400">No procedures logged yet — add them on the Procedures page.</p>}
            </div>
          </div>
        </div>
        {procRev?.monthly?.length > 0 && (
          <div className="mt-5 pt-5 border-t border-slate-100" data-testid="revenue-trend-chart">
            <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">6-Month Revenue Trend</div>
            <ResponsiveContainer width="100%" height={160}>
              <AreaChart data={procRev.monthly} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
                <defs>
                  <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0891b2" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#0891b2" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={48} tickFormatter={(v) => fmtCurrency(v, true)} />
                <Tooltip formatter={(v) => fmtCurrency(v)} labelStyle={{ color: "#334155" }} />
                <Area type="monotone" dataKey="revenue" stroke="#0891b2" strokeWidth={2} fill="url(#revGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
        {procRev?.by_provider?.length > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-100" data-testid="revenue-by-provider">
            <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">Revenue by Provider</div>
            <div className="space-y-1.5">
              {procRev.by_provider.slice(0, 5).map((p) => (
                <div key={p.name} className="flex items-center gap-3">
                  <div className="min-w-0 flex-1 text-sm text-slate-700 truncate">{p.name}</div>
                  <div className="text-xs text-slate-400">{p.count}×</div>
                  <div className="font-mono-metric font-semibold text-slate-900 w-24 text-right">{fmtCurrency(p.revenue, true)}</div>
                </div>
              ))}
            </div>
            {procRev.by_provider.length === 1 && procRev.by_provider[0].name === "Unassigned" && (
              <p className="text-[11px] text-slate-400 mt-1.5">Tip: choose a provider when logging a procedure (or connect DrChrono billing) to split revenue by provider.</p>
            )}
          </div>
        )}
      </motion.div>

      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm mt-6" data-testid="new-patients-trend-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-heading font-bold text-slate-900">New Patients Trend</h3>
            <p className="text-xs text-slate-500">First-visit patients · last 6 months</p>
          </div>
          <span className="h-9 w-9 rounded-lg bg-cyan-50 text-cyan-600 flex items-center justify-center">
            <Users className="h-[18px] w-[18px]" />
          </span>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={stats?.new_patients_monthly || []} margin={{ left: -4, right: 8, top: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} allowDecimals={false} width={40} />
            <Tooltip cursor={{ fill: "#f8fafc" }} contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }} formatter={(v) => [`${v} new`, "Patients"]} />
            <Bar dataKey="count" fill="#0891b2" radius={[4, 4, 0, 0]} maxBarSize={44} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <ReorderReport />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        {/* Funnel */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-heading font-bold text-slate-900">Patient Acquisition Funnel</h3>
              <p className="text-xs text-slate-500">Leads → Consult → Active treatment</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={funnel} margin={{ left: -18, right: 8, top: 8 }}>
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#06B6D4" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#06B6D4" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis dataKey="stage" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }} />
              <Area type="monotone" dataKey="count" stroke="#0891b2" strokeWidth={2.5} fill="url(#g1)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* DrChrono widget */}
        <div className="bg-slate-950 text-white rounded-xl p-6 shadow-sm flex flex-col">
          <div className="flex items-center gap-2 mb-1">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 pulse-dot" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
            </span>
            <span className="text-sm font-semibold text-emerald-300">DrChrono EMR Connected</span>
          </div>
          <p className="text-xs text-slate-400 mb-5">Mocked EMR interface · live sync simulation</p>
          <dl className="space-y-3 text-sm flex-1">
            <div className="flex justify-between"><dt className="text-slate-400">API Latency</dt><dd className="font-mono-metric text-cyan-300">{dc?.latency_ms ?? "—"}ms</dd></div>
            <div className="flex justify-between"><dt className="text-slate-400">Patients Synced</dt><dd className="font-mono-metric">{dc?.patients_synced ?? "—"}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-400">Appointments</dt><dd className="font-mono-metric">{dc?.appointments_synced ?? "—"}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-400">Last Synced</dt><dd className="text-slate-300 text-xs">{dc?.last_synced ? new Date(dc.last_synced).toLocaleTimeString() : "—"}</dd></div>
          </dl>
          <Button data-testid="dashboard-sync-button" onClick={() => sync.mutate()} disabled={sync.isPending}
            className="mt-5 w-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold">
            <RefreshCw className={`h-4 w-4 mr-2 ${sync.isPending ? "animate-spin" : ""}`} /> Run Manual Sync
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {/* No-show feed */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2 mb-4">
            <AlertTriangle className="h-4 w-4 text-amber-500" /> No-Show Risk Alerts
          </h3>
          <div className="space-y-3">
            {highRisk.length === 0 && <p className="text-sm text-slate-400">No high-risk appointments. </p>}
            {highRisk.map((a) => (
              <div key={a.id} className="flex items-center justify-between border border-slate-100 rounded-lg px-3 py-2.5 hover:bg-slate-50 transition-colors">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-800 truncate">{a.patient_name}</div>
                  <div className="text-xs text-slate-500 truncate">{a.type} · {a.risk_reason}</div>
                </div>
                <span className={`text-[11px] font-semibold px-2 py-1 rounded-full border shrink-0 ${riskColor[a.no_show_risk]}`}>HIGH</span>
              </div>
            ))}
          </div>
        </div>

        {/* Referral leaderboard */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2 mb-4">
            <Trophy className="h-4 w-4 text-cyan-500" /> Referral Leaderboard
          </h3>
          <div className="space-y-3">
            {referrals.slice(0, 5).map((r, i) => (
              <div key={r.id} className="flex items-center gap-3">
                <span className="h-7 w-7 rounded-lg bg-slate-100 text-slate-700 text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-800 truncate">{r.name}</div>
                  <div className="text-xs text-slate-500">{r.patients_referred} patients referred</div>
                </div>
                <div className="text-sm font-mono-metric font-semibold text-emerald-600 flex items-center gap-0.5">
                  {fmtCurrency(r.revenue_generated, true)} <ArrowUpRight className="h-3 w-3" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}
